import React, { useEffect, useState, useCallback } from "react";
import { Flag, CheckCircle, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

/* ============================================================
   Admin — Duplicate Reports Queue
   Reader-flagged duplicate stories ready for editorial review.
   ============================================================ */

const STATUS_FILTERS = [
  { id: "pending",   label: "Pending" },
  { id: "confirmed", label: "Confirmed Dupes" },
  { id: "dismissed", label: "Dismissed" },
  { id: "all",       label: "All" },
];

const timeAgoShort = (iso) => {
  if (!iso) return "—";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

export default function DuplicateReports() {
  const [status, setStatus] = useState("pending");
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${BASE_URL}/api/admin/duplicate-reports?status=${status}&page=${page}&limit=${LIMIT}`,
        { credentials: "include" }
      );
      const d = await r.json();
      setReports(d.reports || []);
      setTotal(d.total || 0);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const act = async (reportId, action) => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin/duplicate-report/${reportId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: action }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(`Action failed: ${err.detail || res.status}`);
        return;
      }
      toast.success(action === "confirmed" ? "Confirmed — story hidden from feed" : "Dismissed");
      await load();
    } catch {
      toast.error("Action failed — network error");
    }
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-10" data-testid="admin-duplicate-reports">
      {/* Status filter */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => { setStatus(f.id); setPage(1); }}
            data-testid={`dupes-filter-${f.id}`}
            className={`h-8 px-4 rounded-full font-mono text-[11px] tracking-[0.1em] uppercase border transition-all ${
              status === f.id
                ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
                : "bg-[var(--paper)] text-[var(--ink-2)] border-[var(--hairline)] hover:border-[var(--ink)]"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          className="ml-auto h-8 w-8 flex items-center justify-center rounded-full border border-[var(--hairline)] text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
          data-testid="dupes-refresh"
          aria-label="Refresh"
        >
          <RefreshCw size={13} strokeWidth={1.75} />
        </button>
        <span className="font-mono text-[10.5px] tracking-[0.12em] uppercase text-[var(--muted)]">
          {total} report{total !== 1 ? "s" : ""}
        </span>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-[14px] border border-[var(--hairline)] animate-pulse bg-[var(--surface-2)]" />
          ))}
        </div>
      ) : reports.length === 0 ? (
        <div className="rounded-[14px] border border-[var(--hairline)] bg-[var(--surface-2)] p-12 text-center">
          <Flag size={28} strokeWidth={1.5} className="mx-auto text-[var(--muted)] mb-3" />
          <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-[var(--muted)]">
            No {status === "all" ? "" : status} reports
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r, i) => {
            const rid = r._id || `${r.story_id}-${i}`;
            return (
              <div
                key={rid}
                data-testid={`dupe-report-${r.story_id}`}
                className="rounded-[14px] border border-[var(--hairline)] bg-[var(--paper)] p-5 flex items-start justify-between gap-4 hover:border-[var(--hairline-strong)] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-[var(--crimson)] border border-[var(--crimson)]/40 rounded-full px-2 py-[2px]">
                      {r.status}
                    </span>
                    <span className="font-mono text-[10px] text-[var(--muted)]">
                      {timeAgoShort(r.reported_at)} ago
                    </span>
                    {r.reported_by && (
                      <span className="font-mono text-[10px] text-[var(--muted)]">
                        · uid {r.reported_by.slice(0, 8)}
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-[15px] leading-[1.3] text-[var(--ink)] line-clamp-2 mb-1">
                    {r.headline || r.story_id}
                  </h3>
                  {r.reason && (
                    <p className="font-mono text-[10.5px] text-[var(--ink-2)] line-clamp-1 italic">
                      "{r.reason}"
                    </p>
                  )}
                  <p className="font-mono text-[9.5px] text-[var(--muted)] mt-1">
                    story_id: {r.story_id}
                  </p>
                </div>

                {r.status === "pending" && (
                  <div className="flex items-center gap-2 shrink-0 mt-1">
                    <button
                      type="button"
                      onClick={() => act(r._id || rid, "confirmed")}
                      data-testid={`dupe-confirm-${r.story_id}`}
                      title="Confirm as duplicate — hides story from feed"
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[var(--crimson)]/50 text-[var(--crimson)] font-mono text-[10px] tracking-[0.1em] uppercase hover:bg-[var(--crimson)] hover:text-white transition-all"
                    >
                      <CheckCircle size={12} strokeWidth={1.75} />
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => act(r._id || rid, "dismissed")}
                      data-testid={`dupe-dismiss-${r.story_id}`}
                      title="Dismiss — not actually a duplicate"
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[var(--hairline)] text-[var(--muted)] font-mono text-[10px] tracking-[0.1em] uppercase hover:border-[var(--ink)] hover:text-[var(--ink)] transition-all"
                    >
                      <X size={12} strokeWidth={1.75} />
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="h-8 px-4 rounded-full border border-[var(--hairline)] font-mono text-[11px] tracking-[0.1em] uppercase text-[var(--ink-2)] disabled:opacity-40 hover:border-[var(--ink)] transition-colors"
          >
            ← Prev
          </button>
          <span className="font-mono text-[11px] text-[var(--muted)]">
            {page} / {Math.ceil(total / LIMIT)}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={page * LIMIT >= total}
            className="h-8 px-4 rounded-full border border-[var(--hairline)] font-mono text-[11px] tracking-[0.1em] uppercase text-[var(--ink-2)] disabled:opacity-40 hover:border-[var(--ink)] transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
